import { theme } from 'antd'
import { createStyles } from 'antd-style'
import { useTranslation } from 'react-i18next'

/**
 * Shared visual chrome for credential list pages (Public access credentials
 * page + the Credentials tab inside an app). Follows the `WorkspaceUsersPage`
 * Pattern-1 shell exactly — same toolbar padding/border, same default AntD
 * table styling — so credential tables read as a continuation of the
 * canonical list style.
 */
export const useCredentialsListStyles = createStyles(({ token }) => ({
  toolbar: {
    padding: '12px 14px',
    borderBottom: `1px solid ${token.colorBorderSecondary}`
  },
  count: {
    color: token.colorText,
    fontSize: 13,
    '& strong': { fontWeight: 600 }
  },
  // Name-column link inside data rows: renders as plain bold body text so
  // the row itself doesn't look "linky"; primary color only appears on
  // hover. Right-click / new-tab still works because the anchor is real.
  table: {
    '& .ant-table-tbody a.name-link': {
      color: token.colorText,
      '&:hover': { color: token.colorPrimary }
    }
  },
  // Match the sizing of `UsersStatusPill` so credential pills read as the
  // same "family" of chip across the app.
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '1px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.55,
    whiteSpace: 'nowrap'
  },
  statusDot: {
    display: 'inline-block',
    width: 6,
    height: 6,
    borderRadius: '50%'
  }
}))

type StatusPillProps = {
  status: string
  /**
   * Optional label override. Defaults to
   * `t('workspace.credentials.status.<status>')`.
   */
  label?: string
}

/**
 * The status pill — soft-tinted rounded chip with a leading dot and colored
 * text (green Active / red Revoked / neutral fallback). Uses the shared
 * `workspace.credentials.status.*` translations by default; pass `label` to
 * override for feature-scoped namespaces.
 */
export const StatusPill = ({ status, label }: StatusPillProps) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { styles } = useCredentialsListStyles()

  const palette =
    status === 'active'
      ? {
          dot: token.colorSuccess,
          text: token.colorSuccess,
          bg: token.colorSuccessBg
        }
      : status === 'revoked'
        ? {
            dot: token.colorError,
            text: token.colorError,
            bg: token.colorErrorBg
          }
        : status === 'expired'
          ? {
              dot: token.colorWarning,
              text: token.colorWarning,
              bg: token.colorWarningBg
            }
          : {
              dot: token.colorTextTertiary,
              text: token.colorText,
              bg: token.colorFillTertiary
            }

  const text = label ?? t(`workspace.credentials.status.${status}`)

  return (
    <span
      className={styles.statusPill}
      style={{ background: palette.bg, color: palette.text }}
    >
      <span className={styles.statusDot} style={{ background: palette.dot }} />
      {text}
    </span>
  )
}
